import { TIcon } from '@components/icons/typescript';
import { IDeliveryPricing } from '@features/checkout/typescript';

export type TPositionType = 'product' | 'delivery' | 'service';

export enum AvailabilityType {
  available = 'available',
  unavailable = 'not_available',
  notEnough = 'not_enough',
}

export type ProductType = {
  id: number;
  name: string;
};

export type ValueType = {
  taxRate: string;
  amount: number;
  currency: string;
};

export type Service = {
  serviceType: string;
  serviceId: string;
  price: ValueType;
};

export type UnitType = {
  base: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  };
  current: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  };
  discounted: number | null;
};
export type PackageType = {
  base: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  };
  current: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  };
  discounted: number | null;
};
export type TotalType = {
  base: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  };
  current: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  };
  discounted: {
    net: ValueType;
    gross: ValueType;
    assembly: ValueType;
  } | null;
};

export type CartTotalType =
  | 'total'
  | 'products'
  | 'services_other'
  | 'deliveries_bring'
  | 'assemblies'
  | 'discounts'
  | 'savings'
  | 'paid_on_delivery'
  | 'deliveries'
  | 'bring_services'
  | 'products_base';

export type ExternalServicesType = 'externalBringingService' | 'externalAssemblyService';

export type CartTotalValueType = {
  amount: number;
  currency: string;
};

export type CartTotalsType = {
  totalType: CartTotalType;
  total: CartTotalValueType;
};

export type CartProductAvailabilityType = {
  dateFrom: string;
  status: AvailabilityType;
  expiryDate: string;
  amount: number;
};

export type CartProductPricesType = {
  unit: UnitType;
  package: PackageType;
  total: TotalType;
  taxRate: string;
};

export type CartProductSizeType = {
  length: number;
  width: number;
  height: number;
  unit: string;
};

export interface ICategoriesProduct {
  categoryId: number;
  code: string;
  name: string;
  parent: number;
}

export type TQuantityType = 'single' | 'multiple' | 'set' | 'package';

export type TUnitType = 'square_meters' | 'piece';

export interface ICartItem {
  parentName: string;
  positionId: string;
  type: string;
  name: string;
  image: string;
  quantity: number;
  quantityType: TQuantityType;
  multiplication: string;
  totalMultiplication: string;
  unitType: TUnitType;
  basePrice: number;
  availability: CartProductAvailabilityType;
  productType: ProductType;
  productSubtype: ProductType;
  prices: CartProductPricesType;
  configuration: {
    pimParentProductId: number;
    pimProductId: number;
    json: string;
    description: string | null;
  };
  size: CartProductSizeType;
  categories: Array<ICategoriesProduct>;
  isAssemblable: boolean;
  isBringable: true;
}

export interface IElement {
  productId: string;
  count: number;
}
export interface IElements {
  [key: number]: IElement;
}

export interface IDelivery {
  customPrice: string;
  deliveryType: string;
  deliveryTypeId: string;
  nameLanguageKey: string;
  image: string;
  isAssembleAvailable: boolean;
  isBringingAvailable: boolean;
  isPaidOnDelivery: boolean;
  isTaxAvailable: boolean;
  isVatAvailable: boolean;
  name: string;
  other: string;
  parcelLocker: string;
  posAddress: string;
  positionId: string;
  prices: CartProductPricesType;
  quantity: number;
  type: string;
}

export type DeliveryType = 'delivery' | 'personal' | 'parcel_locker';

interface AdditionalFieldRequirement {
  exist: boolean;
  required: boolean;
}

interface AdditionalFields {
  comment: AdditionalFieldRequirement;
  customPrice: AdditionalFieldRequirement;
  parcelLocker: AdditionalFieldRequirement;
  pickupPointId: AdditionalFieldRequirement;
}

interface Requirements {
  retailCustomer: boolean;
  assemblyService: boolean;
  bringService: boolean;
}
export interface ICartAvailableDeliveryConfiguration {
  additionalFields: AdditionalFields | null;
  requirements: Requirements | null;
}

export interface ICartAvailableDelivery {
  deliveryType: DeliveryType;
  deliveryTypeId: string;
  deliveryTypeNameLanguageKey: string;
  isAssembleAvailable: boolean;
  isBringingAvailable: boolean;
  isPaidOnDelivery: boolean;
  isTaxAvailable: boolean;
  isVatAvailable: boolean;
  leadTime: number;
  logisticsTime: number;
  personalPickupStoreType: unknown;
  price: CartTotalValueType | null;
  postalCode: string;
  estimatedTime?: number;
  parcelLocker?: string;
  pickupPointId?: string;
  isBringingSelected?: boolean;
  configuration: ICartAvailableDeliveryConfiguration;
  pricingWithRequiredServices: IDeliveryPricing;
  expectedDeliveryDate?: string;
}

export interface IQuickDeliveryMessage {
  message: string;
  type: 'positive' | 'negative';
  postalCode?: undefined | string;
}
export interface IAdditionalArgument {
  key: string;
  value: string;
}

export interface IAdditionalArguments {
  arguments: IAdditionalArgument[];
}

// Alternatywnie - bardziej elastyczne rozwiązanie
export type IAdditionalArgumentsRecord = Record<string, string>;

export type TAdditionalDeliveryAddressParameter = Record<string, string>;

export interface ITransportsCartAvailableDelivery {
  [key: string]: ICartAvailableDelivery;
}

export interface ICartService {
  description: string | null;
  discount: unknown;
  discounted: number | null;
  image: string | null;
  isActive: boolean;
  isPaidOnDelivery: boolean;
  name: string;
  positionId: string;
  prices: CartProductPricesType;
  quantity: number;
  rejectionReasonCode: string | null;
  serviceId: string;
  status: string;
  type: string;
  unitPrice: number;
  unitQuantity: number;
  unitType: string;
}

export interface ICartDataConfigurationElement {
  availability: number;
  count: number;
  materialName: string;
  name: string;
  productId: number | string;
  title?: string;
  config?: string[];
}

export interface ICartDataConfiguration {
  type: string;
  productId: number;
  productName: string;
  thumb: string;
  elements: Array<ICartDataConfigurationElement>;
}

export interface ICartData {
  productId: number;
  configuration: string | ICartDataConfiguration;
  quantity: number;
  name: string;
  image: string | null;
  price?: number;
}

export interface IBasicElementItem {
  type: string;
  name: string;
}

export interface IBasicConfigTree {
  quantity: number;
  material_name: string;
  product_id: string;
  image: string;
  element: Record<string, IBasicElementItem>;
  price: string;
}

export interface IConfiguratorBasicProduct {
  configAsTree: Record<string, IBasicConfigTree>;
  product_id: number;
}

export interface ICreativeProductDetail {
  name: string;
  value: string;
}

export interface ICreativeUuid {
  uuid: string;
}

export interface ICreativeImages {
  main: string;
  inner: string;
  back: string;
}

export interface ICreativeName {
  name: string;
}

export interface ICreativeImageGcs {
  CART: string;
  FULL: string;
}

export interface ICreativeImagesGcs {
  back: ICreativeImageGcs;
  inner: ICreativeImageGcs;
  main: ICreativeImageGcs;
}

export interface ICreativeExtraData {
  productDetails: Array<ICreativeProductDetail>;
  objectUuid: ICreativeUuid;
  name: ICreativeName;
  images: ICreativeImagesGcs;
  iguanaUploadData?: {
    forceAssetModelName: string;
    ibisIdentifier: string;
    ibisSpace: string;
    imageUploaded: boolean;
    jsonUploaded: boolean;
    packageCreated: boolean;
    pricesUploaded: boolean;
  };
}

export interface ICreativeShaders {
  selectedHandleName: string;
  selectedFrontName: string;
  selectedWallName: string;
  selectedShelfName: string;
  selectedBodyName: string;
  selectedLegsName: string;
  selectedRafixName: string;
  selectedSupportName: string;
  selectedFrameName: string;
}

export interface ICreativePosition {
  x: number;
  y: number;
}

export interface ICreativeCenter {
  x: number;
  y: number;
  z: number;
}

export interface ICreativeRotation {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface ICreativeSlot {
  position: ICreativePosition;
  shape: ICreativePosition;
  url: string;
}

export interface ICreativeElement {
  model: string;
  light: string;
  type: number;
  specyficType: number;
  shaderName: string;
  position: ICreativePosition;
  shape: ICreativePosition;
  center: ICreativeCenter;
  rotation: ICreativeRotation;
  bindings: Array<number>;
  slot: ICreativeSlot;
  opens?: number;
}

export interface ICreativeBack {
  position: ICreativePosition;
  shape: ICreativePosition;
  model: string;
  light: string;
  shaderName: string;
  meshes: unknown;
  isSelected: boolean;
  _width: number;
  _height: number;
  _depth: number;
  _specyficType: number;
  _bindings: unknown;
  _price: number;
  _type: number;
}

export interface ICreativeCreativePrice {
  currency: string;
  currencySymbol: string;
  currentPrice: number;
  previousPrice: number;
}

export interface ICreativeElementsErp {
  feature: string;
  type: string;
  color: string;
}

export interface ICreativeDimension {
  width: number;
  height: number;
  depth: number;
}

export interface ICreativeProductPreset {
  id: string;
  code: string;
  codeErp: string;
  name: string;
  dimension: ICreativeDimension;
  dimension_full: ICreativeDimension;
}

export interface ICreativePivot {
  x: number;
  y: number;
  z: number;
}

export interface ICreativeData {
  shaders: ICreativeShaders;
  elements: Array<ICreativeElement>;
  backs: Array<ICreativeBack>;
  creative_price: ICreativeCreativePrice;
  elements_erp: Array<ICreativeElementsErp>;
  productPreset: ICreativeProductPreset;
  pivot: ICreativePivot;
  tapes: string;
  productDetails: Array<ICreativeProductDetail>;
}

export interface IConfiguratorCreativeProduct {
  id: number;
  data: ICreativeData;
  images: ICreativeImages;
  count: number;
  extraData: ICreativeExtraData;
}

export interface ISimpleElement {
  availability: number;
  count: number;
  material_name: string;
  name: string;
  product_id: string;
  title: string;
}

export interface IConfiguratorSimpleProduct {
  elements: Array<ISimpleElement>;
  product_name: string;
  product_id: string;
  thumb: string;
  price: string;
}

export interface IStatusStep {
  name: string;
  pathname: string;
  alert: boolean;
  stepID: number;
}

export type PaymentType = {
  id: number;
  icon: TIcon;
  width: number;
  link: string;
  title: string;
};

export interface ICartOptionType {
  itemId: string;
  icon: TIcon;
  title: string;
  subTitle: string | null;
  priceDescription: string | null;
  price: number;
  currency: string;
}

export interface ICartOption {
  title: string;
  options: Array<ICartOptionType>;
  description: Array<string>;
  optionsHeader?: string;
}

export interface ICartPage {
  cartId: string;
  paymentOptions: Array<PaymentType>;
  deliveryOptions: ICartOption;
  assemblyService: ICartOption;
  instalmentPaymentOptions: ICartOption;
}

export interface ICoupon {
  id: string;
  code: string;
  partner: string;
  campaign: string;
}

export interface IDiscountData {
  discountCode: string;
  validationStatus: boolean;
  message: string;
  discountValue: string | false;
  coupon: ICoupon | null;
}

export interface IFrontConfiguratorData {
  cartData: {
    products: ICartData[];
  };
  productId: string;
  objectUuid: string;
  name: string;
}

export interface IFrontConfiguratorImages {
  dedicated: string;
}
export interface IFrontConfiguratorProduct {
  id: number;
  data: IFrontConfiguratorData;
  images: IFrontConfiguratorImages;
  count: number;
}
export interface IAsset {
  code: string;
  url: string;
}

export interface IFrontConfiguratorQuoteRequest {
  code: string;
  name: string;
  selected: boolean;
  disabled: boolean;
  assets?: IAsset[];
  productIds: [
    {
      id: number;
      parentId: number | null | undefined;
    },
  ];
}
